import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Text, Heading } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import { Gql } from '@fiverr-private/dynamic_matching';
import type { Currency } from '@fiverr-private/currencies_plugin';
import { centsToCurrency } from '../../utils/formatCurrency';
import { formatDate } from '../../utils/formatDate';
import type { SellerBriefResponse } from '../../../types/SellerBriefResponse';

interface BudgetAndTimelineProps {
  pricingType: SellerBriefResponse['pricingType'];
  budgetInCents: SellerBriefResponse['budgetInCents'];
  deliveryDate: SellerBriefResponse['deliveryDate'];
  timeline: SellerBriefResponse['timeline'];
  currency?: Currency;
}

const pricingTypeTimelines = [Gql.DmBriefTimeline.SoonAsPossible, Gql.DmBriefTimeline.Flexible];

const Timeline = ({
  timeline,
  deliveryDate,
  pricingType,
}: {
  timeline: SellerBriefResponse['timeline'];
  deliveryDate: SellerBriefResponse['deliveryDate'];
  pricingType: SellerBriefResponse['pricingType'];
}) => {
  if (timeline === Gql.DmBriefTimeline.SpecificDate)
    return (
      <Stack direction="column" gap="1">
        <Heading size="h_xxs">
          <I18n k="brief_preview_drawer.brief.timeline.deliver_on" />
        </Heading>
        <Text>{formatDate(deliveryDate)}</Text>
      </Stack>
    );

  return (
    <Stack direction="column" gap="1">
      <Heading size="h_xxs">
        <I18n k="brief_preview_drawer.brief.timeline.timeline" />
      </Heading>
      <Text>
        {pricingTypeTimelines.includes(timeline) ? (
          <I18n
            k={`brief_preview_drawer.brief.timeline.type.${pricingType?.toLowerCase()}.${timeline?.toLowerCase()}`}
          />
        ) : (
          <I18n k="brief_preview_drawer.brief.timeline.type.ongoing" />
        )}
      </Text>
    </Stack>
  );
};

export const BudgetAndTimeline: React.FC<BudgetAndTimelineProps> = ({
  pricingType,
  budgetInCents,
  deliveryDate,
  timeline,
  currency,
}) => (
  <Stack gap="12">
    {pricingType === Gql.BriefPricingType.Hourly ? (
      <Stack direction="column" gap="1">
        <Heading size="h_xxs">
          <I18n k={'brief_preview_drawer.brief.budget.hourly_rate'} />
        </Heading>
        <Text>
          {centsToCurrency(budgetInCents, currency)} / <I18n k={'brief_preview_drawer.brief.budget.hour'} />
        </Text>
      </Stack>
    ) : (
      <Stack direction="column" gap="1">
        <Heading size="h_xxs">
          <I18n k="brief_preview_drawer.brief.budget.fixed" />
        </Heading>
        <Text>{centsToCurrency(budgetInCents, currency)}</Text>
      </Stack>
    )}
    <Timeline pricingType={pricingType} timeline={timeline} deliveryDate={deliveryDate} />
  </Stack>
);
